/**
 * Karte eines Kanals (Board „Preisautomatik · Einstellungen“): Zeichen, deutscher Stand des letzten
 * Auftrags, Aufschlag „Aus“ oder „Prozentual erhöhen“. Während ein Auftrag läuft, ist die Karte
 * gesperrt; Fehler aus Vorschau und Auftrag stehen an der Karte. Das Beispiel kommt nur vom Server.
 */
import { useEffect, useRef, useState } from 'react';
import { useApiProblem } from '@/shared/api';
import type { ChannelPriceModifierItem, ChannelPriceModifierPreview } from '@/shared/api';
import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/format';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { ChannelModifierConfirm } from './ChannelModifierConfirm';
import { ChannelModifierFields } from './ChannelModifierControls';
import {
  PENDING,
  channelErrorText,
  channelPreviewProblem,
  channelSaveProblem,
  channelStatus,
} from './channelPriceText';

interface Props {
  item: ChannelPriceModifierItem;
  currency: string;
  saving: boolean;
  previewing: boolean;
  saveError?: unknown;
  /** Der Stand ist seit dem letzten Auftrag neu geladen. */
  reloaded: boolean;
  onPreview: (value: number | null) => Promise<ChannelPriceModifierPreview>;
  onSave: (value: number | null, concurrency: number) => void;
}

const TONE = {
  green: 'bg-forest-soft text-forest-deep',
  amber: 'bg-amber-tint text-amber-ink',
  red: 'bg-sealwax-tint text-sealwax',
  grey: 'bg-card-alt text-ink-soft',
};

export function ChannelModifierCard({ item, currency, saving, previewing, ...props }: Props) {
  const [enabled, setEnabled] = useState(item.mode === 'IncreasePercent');
  const [value, setValue] = useState(item.value ?? 10);
  const [preview, setPreview] = useState<ChannelPriceModifierPreview | null>(null);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const saveRef = useRef<HTMLButtonElement>(null);
  const saveText = channelSaveProblem(useApiProblem(props.saveError ?? null), props.reloaded);
  const previewText = channelPreviewProblem(useApiProblem(previewError));
  useEffect(() => {
    setEnabled(item.mode === 'IncreasePercent');
    setValue(item.value ?? 10);
  }, [item.mode, item.value]);
  const pending = PENDING.includes(item.operationStatus ?? '');
  const status = channelStatus(item.operationStatus);
  const invalid = enabled && (value <= 0 || value > 100);
  const changed = enabled
    ? item.mode !== 'IncreasePercent' || item.value !== value
    : item.mode !== 'Off';
  const requestPreview = async () => {
    setPreviewError(null);
    try {
      setPreview(await props.onPreview(enabled ? value : null));
    } catch (error) {
      setPreviewError(error);
    }
  };
  const external = item.mode === 'ExternalReadOnly';
  return (
    <article className="bg-card flex min-w-0 flex-col gap-2.5 rounded-lg p-3.5">
      <span className="flex items-center gap-2">
        <OtaLogo channel={item.channel} size={24} />
        <span className="min-w-0 grow truncate text-sm font-bold">{item.title}</span>
        <span className={cn('rounded-pill px-2 py-0.5 text-[11px] font-bold', TONE[status.tone])}>
          {status.label}
        </span>
      </span>
      {external ? (
        <p className="text-ink-soft text-[13px] leading-normal">
          Extern in Channex gepflegt
          {item.value !== null && ` (${item.value > 0 ? '+' : ''}${item.value} %)`}. Hier nur
          sichtbar, nicht änderbar.
        </p>
      ) : (
        <ChannelModifierFields
          enabled={enabled}
          value={value}
          disabled={pending}
          saveDisabled={pending || saving || previewing || invalid || !changed}
          onEnabled={setEnabled}
          onValue={setValue}
          onSave={() => void requestPreview()}
          saveRef={saveRef}
        />
      )}
      {pending && (
        <p role="status" className="text-amber-ink text-xs">
          Bis Channex den Auftrag bestätigt, ist die Karte gesperrt.
        </p>
      )}
      {item.exampleDerivedRate !== null && (
        <span className="text-ink-soft text-xs">
          Beispiel: {formatMoney(item.exampleBaseRate, currency)} Basis →{' '}
          {formatMoney(item.exampleDerivedRate, currency)} Kanalpreis
        </span>
      )}
      {enabled && !external && (
        <span className="text-ink-soft text-xs">
          Ein Kanalaufschlag darf über dem Höchstpreis der Preisautomatik liegen.
        </span>
      )}
      {item.errorCode && (
        <p role="alert" className="text-sealwax text-xs">
          {channelErrorText(item.errorCode)} <code className="font-mono">{item.errorCode}</code>
        </p>
      )}
      {previewing && (
        <p role="status" className="text-ink-soft text-xs">
          Die Vorschau wird serverseitig berechnet …
        </p>
      )}
      {(previewText || saveText) && (
        <p role="alert" className="text-sealwax text-xs">
          {previewText ?? saveText}
        </p>
      )}
      {preview && (
        <ChannelModifierConfirm
          preview={preview}
          currency={currency}
          onCancel={() => {
            setPreview(null);
            saveRef.current?.focus();
          }}
          onConfirm={() => {
            props.onSave(preview.draftIncreasePercent, preview.concurrency);
            setPreview(null);
          }}
        />
      )}
    </article>
  );
}
